import type { FiltersState, FilterValue } from '@components/ui/FilterMenu';

import type { ClaimWorkSortKey } from '../../../store';
import { isQueueViewKey } from '../../../types/encounterWork';

import { type ClaimRowGroupBy, isClaimRowGroupBy } from './claimRowGrouping';
import {
  createDefaultClaimWorkPredicates,
  dateRangeForWindow,
  DEFAULT_SERVICE_DATE_WINDOW_DAYS,
  RELATIVE_DATE_WINDOWS,
} from './claimWorkFilters';
import type { StoredClaimWorkViewKey } from './claimWorkViewGroups';
import {
  DEFAULT_CLAIM_WORK_VIEW,
  isStoredClaimWorkViewKey,
  resolveClaimWorkViewKey,
} from './claimWorkViewGroups';

/**
 * The Claims worklist's canonical state, externalized to URL query params
 * (GDN-3196). The URL is the source of truth: any filter/view combination is
 * a reload-safe, shareable address — the natural completion of org-wide
 * saved views.
 *
 * Rules this module owns:
 * - It reads and writes ONLY its own keys; `tab` and any foreign params pass
 *   through untouched (BillingV2 owns `tab`).
 * - Defaults serialize to ABSENCE — the pristine `/jotbill?tab=claims` URL
 *   stays clean, and equals the default list.
 * - Parsing never throws: a malformed value falls back to that field's
 *   default. A wrong URL yields a right list.
 * - "All time" is explicit (`window=all`), because absence already means the
 *   default 90-day scope — a cleared filter must survive reload as cleared.
 * - Row selection and page cursors are deliberately NOT here: selection is
 *   never restored (a checked batch going stale underneath you is worse than
 *   re-checking), and cursors are server-opaque ephemera (sessionStorage,
 *   best-effort — see ClaimsTab).
 */

export interface ClaimWorkUrlState {
  /**
   * The key AS STORED, which is what the server is asked for. A saved view or
   * an old link on `needs_correction` keeps that key: the server reads it as
   * the union of review and fix, and narrowing it to `fix` here would quietly
   * drop half the rows the view was saved to hold. `railViewKey` resolves it
   * for the rail, and only for the rail.
   */
  workView: StoredClaimWorkViewKey;
  savedViewId: string | null;
  predicates: FiltersState;
  sort: ClaimWorkSortKey;
  perPage: number;
  /** "Show archived and snoozed" (GDN-3381). Queue views only; a list never
   *  hides a row, so the flag is meaningless there and never serialized. */
  showHidden: boolean;
  /** How the loaded rows are grouped (GDN-3383). Claims to fix only, and
   *  purely a rendering choice: it never reaches the query. In the URL, like
   *  every other list decision, so a grouped queue is a shareable address. */
  groupBy: ClaimRowGroupBy;
}

/** The sorts the worklist UI offers (a subset of the server's). */
const SORT_KEYS: ClaimWorkSortKey[] = [
  'service_date_desc',
  'service_date_asc',
  'charge_desc',
  'charge_asc',
];

const PER_PAGE_OPTIONS = [25, 50, 100];
export const DEFAULT_CLAIM_WORK_PER_PAGE = 50;

/** Multi-select filters, param key ↔ predicate field id. */
const MULTI_SELECT_PARAMS: Array<{ param: string; fieldId: string }> = [
  { param: 'provider', fieldId: 'provider' },
  { param: 'payer', fieldId: 'payer' },
  { param: 'patient', fieldId: 'patient' },
  { param: 'status', fieldId: 'claimLifecycle' },
];

/** Every param key this module owns — cleared before writing, never more. */
export const CLAIM_WORK_PARAM_KEYS = [
  'view',
  'saved',
  ...MULTI_SELECT_PARAMS.map(entry => entry.param),
  'window',
  'from',
  'to',
  'sort',
  'per',
  'hidden',
  'group',
] as const;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Strict calendar validity — `Date.parse` NORMALIZES shapes like 2026-02-31
 * (to March 3rd) instead of rejecting them, which would forward the original
 * malformed string into the claims query. Valid means the date survives a
 * round trip unchanged.
 */
const isIsoDate = (value: string): boolean => {
  if (!ISO_DATE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

const csv = (values: string[]): string => values.join(',');

const parseCsv = (value: string | null): string[] =>
  value
    ? value
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
    : [];

export function defaultClaimWorkUrlState(): ClaimWorkUrlState {
  return {
    workView: DEFAULT_CLAIM_WORK_VIEW,
    savedViewId: null,
    predicates: createDefaultClaimWorkPredicates(),
    sort: 'service_date_desc',
    perPage: DEFAULT_CLAIM_WORK_PER_PAGE,
    showHidden: false,
    groupBy: 'none',
  };
}

/**
 * URL → state. Total: every malformed field independently falls back to its
 * default, so a mangled shared link still lands on a working list.
 */
export function parseClaimWorkUrlState(params: URLSearchParams): ClaimWorkUrlState {
  const view = params.get('view');
  const group = params.get('group');
  const sort = params.get('sort');
  const per = Number(params.get('per'));
  const saved = params.get('saved');

  const predicates: FiltersState = {};
  for (const { param, fieldId } of MULTI_SELECT_PARAMS) {
    const values = parseCsv(params.get(param));
    if (values.length > 0) predicates[fieldId] = { kind: 'multiSelect', values };
  }

  const window = params.get('window');
  const from = params.get('from');
  const to = params.get('to');
  if (window === 'all') {
    // Explicitly cleared — no serviceDate predicate ("All time").
  } else if (window !== null) {
    const days = Number(window);
    if (Number.isInteger(days) && days >= 1 && days <= 3650) {
      predicates.serviceDate = dateRangeForWindow(days);
    } else {
      predicates.serviceDate = dateRangeForWindow(DEFAULT_SERVICE_DATE_WINDOW_DAYS);
    }
  } else if ((from && isIsoDate(from)) || (to && isIsoDate(to))) {
    predicates.serviceDate = {
      kind: 'dateRange',
      start: from && isIsoDate(from) ? from : null,
      end: to && isIsoDate(to) ? to : null,
    };
  } else {
    predicates.serviceDate = dateRangeForWindow(DEFAULT_SERVICE_DATE_WINDOW_DAYS);
  }

  /* Kept as stored, aliases included: the server owns what each key means,
     and `needs_correction` means MORE rows than `fix`, not the same ones. */
  const workView = isStoredClaimWorkViewKey(view) ? view : DEFAULT_CLAIM_WORK_VIEW;

  return {
    workView,
    savedViewId: saved && saved.trim() ? saved : null,
    predicates,
    sort: SORT_KEYS.includes(sort as ClaimWorkSortKey)
      ? (sort as ClaimWorkSortKey)
      : 'service_date_desc',
    perPage: PER_PAGE_OPTIONS.includes(per) ? per : DEFAULT_CLAIM_WORK_PER_PAGE,
    showHidden: params.get('hidden') === '1',
    groupBy: isClaimRowGroupBy(group) ? group : 'none',
  };
}

const multiSelectValues = (predicates: FiltersState, fieldId: string): string[] => {
  const value = predicates[fieldId];
  return value?.kind === 'multiSelect' ? value.values : [];
};

const serviceDateOf = (
  predicates: FiltersState,
): Extract<FilterValue, { kind: 'dateRange' }> | undefined => {
  const value = predicates.serviceDate;
  return value?.kind === 'dateRange' ? value : undefined;
};

/**
 * State → params, into a COPY of the given params. Only this module's keys
 * are touched; a default-valued field serializes to absence.
 */
export function applyClaimWorkUrlState(
  params: URLSearchParams,
  state: ClaimWorkUrlState,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of CLAIM_WORK_PARAM_KEYS) next.delete(key);

  if (state.workView !== DEFAULT_CLAIM_WORK_VIEW) next.set('view', state.workView);
  if (state.savedViewId) next.set('saved', state.savedViewId);
  /* Off is absence, like every other default here. The name is shared with
     the Home empty-state link (packet C), which arrives as `hidden=1`.
     A list cannot hide a row, so the flag is dropped rather than carried
     into a link where it would say nothing and look like it did. */
  if (state.showHidden && isQueueViewKey(railViewKey(state.workView))) next.set('hidden', '1');
  /* Ungrouped is absence. The value is the grouping key itself, so the
     address says what the reader will see. Same rule as `hidden`: only
     Claims to fix offers grouping, so the param is dropped everywhere else
     rather than left behind describing a list nobody is looking at. */
  if (state.groupBy !== 'none' && railViewKey(state.workView) === 'fix') {
    next.set('group', state.groupBy);
  }

  for (const { param, fieldId } of MULTI_SELECT_PARAMS) {
    const values = multiSelectValues(state.predicates, fieldId);
    if (values.length > 0) next.set(param, csv(values));
  }

  const serviceDate = serviceDateOf(state.predicates);
  /* A UI-picked preset can carry `preset` without `windowDays` — the same
     either-or derivation ClaimsTab uses for its query args. Serializing it
     as a window keeps the scope RELATIVE across reloads instead of freezing
     the dates it happened to resolve to today. */
  const windowDays =
    serviceDate?.windowDays ??
    (serviceDate?.preset ? RELATIVE_DATE_WINDOWS[serviceDate.preset] : undefined);
  if (!serviceDate) {
    // A cleared date scope must survive reload as cleared — absence would
    // read as the default window.
    next.set('window', 'all');
  } else if (windowDays) {
    if (windowDays !== DEFAULT_SERVICE_DATE_WINDOW_DAYS) {
      next.set('window', String(windowDays));
    }
  } else {
    if (serviceDate.start) next.set('from', serviceDate.start);
    if (serviceDate.end) next.set('to', serviceDate.end);
    if (!serviceDate.start && !serviceDate.end) next.set('window', 'all');
  }

  if (state.sort !== 'service_date_desc') next.set('sort', state.sort);
  if (state.perPage !== DEFAULT_CLAIM_WORK_PER_PAGE) next.set('per', String(state.perPage));

  return next;
}

/**
 * Canonical-state signature for keying ephemera (cursor stack, scroll) in
 * sessionStorage. Built from this module's OWN serialization, so two URLs
 * that mean the same list share ephemera and foreign params never split it.
 */
export function claimWorkStateSignature(state: ClaimWorkUrlState): string {
  const params = applyClaimWorkUrlState(new URLSearchParams(), state);
  /* Grouping is deliberately NOT part of the signature. It reorders rows
     that already arrived and changes no query argument, so letting it
     invalidate the cursor stack would throw a biller on page 4 back to page
     1 for pressing a rendering control. */
  params.delete('group');
  return params.toString();
}

/**
 * The rail item a stored key lights up. Presentation only: never send this to
 * the server in place of the stored key.
 */
export const railViewKey = (workView: StoredClaimWorkViewKey) =>
  resolveClaimWorkViewKey(workView) ?? DEFAULT_CLAIM_WORK_VIEW;
